import React from 'react';
import { ArrowLeft, BookOpen, Target, Sparkles, ShieldCheck, HeartHandshake, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const AboutView = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 p-6 md:p-12">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 sm:p-10">
        
        {/* Tillbaka-knapp */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors cursor-pointer"
        >
          <ArrowLeft size={16} />
          {t(language, 'common_back') || 'Back'}
        </button>

        {/* Header */}
        <div className="border-b border-slate-100 dark:border-slate-700 pb-6 mb-8">
          <div className="inline-flex p-3 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded-2xl mb-4 shadow-sm">
            <BookOpen size={28} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 dark:text-white tracking-tight">
            {t(language, 'about_title') || 'About Science Monitor'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm sm:text-base mt-2">
            {t(language, 'about_subtitle') || 'Democratizing peer-reviewed science for curious minds and specialists alike.'}
          </p>
        </div>

        {/* Huvudinnehåll */}
        <div className="space-y-8 text-sm sm:text-base leading-relaxed text-slate-600 dark:text-slate-300">
          
          {/* Mission */}
          <section>
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-lg mb-2">
              <Target size={20} className="text-indigo-600 dark:text-indigo-400" />
              <h2>{t(language, 'about_mission_title') || 'Our Mission'}</h2>
            </div>
            <p>
              {t(language, 'about_mission_desc') || 
                'Every day, thousands of groundbreaking scientific discoveries are published across global repositories. Yet, most of this knowledge remains locked behind dense academic jargon, language barriers, and fragmented databases. Science Monitor was created to bridge this gap — making authentic, peer-reviewed science accessible, structured, and readable for everyone.'}
            </p>
          </section>

          {/* Bakgrund / Grundaren */}
          <section className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-700/40 border border-slate-200/80 dark:border-slate-700">
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-lg mb-2">
              <GraduationCap size={20} className="text-indigo-600 dark:text-indigo-400" />
              <h2>{t(language, 'about_founder_title') || 'Behind Science Monitor'}</h2>
            </div>
            <p className="text-sm sm:text-base leading-relaxed">
              {t(language, 'about_founder_desc') || 
                'Science Monitor was founded by Fredrik Mokvist, PhD in Chemistry with extensive leadership experience in the pharmaceutical and Life Science sectors. Having navigated both rigorous academic lab environments and commercial clinical landscapes, the platform was born out of a firsthand realization: the world produces invaluable scientific progress every single day, but the tools to follow, understand, and synthesize that research have not kept pace — until now.'}
            </p>
          </section>

          {/* How It Works */}
          <section>
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-lg mb-2">
              <Sparkles size={20} className="text-blue-600 dark:text-blue-400" />
              <h2>{t(language, 'about_how_title') || 'How It Works'}</h2>
            </div>
            <p>
              {t(language, 'about_how_desc') || 
                'We monitor open scientific registries across millions of publications daily. When new research matches your defined interests — whether it is quantum mechanics, literature, or targeted clinical therapies — our processing pipeline synthesizes the findings into clean, readable briefings tailored to your chosen complexity level.'}
            </p>
          </section>

          {/* Principer */}
          <section>
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-lg mb-3">
              <ShieldCheck size={20} className="text-emerald-600 dark:text-emerald-400" />
              <h2>{t(language, 'about_principles_title') || 'Core Principles'}</h2>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-1">
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-700">
                <h3 className="font-bold text-slate-900 dark:text-white text-sm mb-1">
                  {t(language, 'about_p1_title') || 'Evidence & Integrity'}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {t(language, 'about_p1_desc') || 
                    'We exclusively process peer-reviewed academic works. No rumors, no clickbait — only verifiable science with direct links to primary sources.'}
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-700">
                <h3 className="font-bold text-slate-900 dark:text-white text-sm mb-1">
                  {t(language, 'about_p2_title') || 'Language Independence'}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {t(language, 'about_p2_desc') || 
                    'Science is published internationally, but knowledge should be accessible in your native language without awkward manual translation.'}
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-700">
                <h3 className="font-bold text-slate-900 dark:text-white text-sm mb-1">
                  {t(language, 'about_p3_title') || 'Privacy by Design'}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {t(language, 'about_p3_desc') || 
                    'We do not sell personal data, track users across the web, or monetize through advertisements. The platform exists solely to deliver insight.'}
                </p>
              </div>
            </div>
          </section>

          {/* Kontakt */}
          <section className="pt-4 border-t border-slate-150 dark:border-slate-700">
            <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white text-lg mb-1">
              <HeartHandshake size={20} className="text-indigo-600 dark:text-indigo-400" />
              <h2>{t(language, 'about_contact_title') || 'Get in Touch'}</h2>
            </div>
            <p className="text-sm">
              {t(language, 'about_contact_desc') || 'Have feedback, questions, or ideas for collaboration? Reach out at'}{' '}
              <a href="mailto:fredrik.mokvist@gmail.com" className="text-indigo-600 dark:text-indigo-400 underline font-medium">
                fredrik.mokvist@gmail.com
              </a>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default AboutView;